import { useRef, useState, useLayoutEffect, type CSSProperties, type ReactNode } from 'react';
import './OverlayIntrinsicSize.css';

export type OverlayIntrinsicSizeVariant = 'popover' | 'centered';

export interface OverlayIntrinsicSizeProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  enabled?: boolean;
  /**
   * popover — анимируются ширина и высота (контент задаёт размер).
   * centered — ширина на всю доступную область (модалка в flex-оверлее), анимируется только высота.
   */
  variant?: OverlayIntrinsicSizeVariant;
}

function readBox(entry: ResizeObserverEntry, fallbackEl: Element) {
  const bs0 = entry.borderBoxSize?.[0];
  if (bs0) {
    return {
      w: Math.ceil(bs0.inlineSize),
      h: Math.ceil(bs0.blockSize),
    };
  }
  const r = entry.contentRect;
  if (r.width > 0 || r.height > 0) {
    return { w: Math.ceil(r.width), h: Math.ceil(r.height) };
  }
  const br = fallbackEl.getBoundingClientRect();
  return { w: Math.ceil(br.width), h: Math.ceil(br.height) };
}

export function OverlayIntrinsicSize({
  children,
  className = '',
  style,
  enabled = true,
  variant = 'popover',
}: OverlayIntrinsicSizeProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const [canAnimate, setCanAnimate] = useState(false);
  const armedRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled) return;
    armedRef.current = false;
    setCanAnimate(false);
    const inner = innerRef.current;
    if (!inner) return;

    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      const { w, h } = readBox(e, inner);
      setBox((prev) => {
        if (prev && prev.w === w && prev.h === h) return prev;
        return { w, h };
      });
      if (!armedRef.current) {
        armedRef.current = true;
        /* centered: без лишнего rAF — иначе 1 кадр задержки у модалок без выигрыша по ширине */
        if (variant === 'centered') {
          setCanAnimate(true);
        } else {
          requestAnimationFrame(() => setCanAnimate(true));
        }
      }
    });
    ro.observe(inner, { box: 'border-box' });
    return () => ro.disconnect();
  }, [enabled, variant]);

  if (!enabled) {
    return <>{children}</>;
  }

  const rootClass = [
    'overlay-intrinsic-size',
    variant === 'centered' ? 'overlay-intrinsic-size--centered' : '',
    canAnimate ? 'overlay-intrinsic-size--animate' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const outerStyle: CSSProperties = {
    ...style,
    height: box?.h,
  };
  if (variant === 'popover' && box) {
    outerStyle.width = box.w;
  }

  return (
    <div className={rootClass} style={outerStyle}>
      <div ref={innerRef} className="overlay-intrinsic-size__inner">
        {children}
      </div>
    </div>
  );
}
